import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type ChangeEvent,
  type KeyboardEvent,
} from 'react';
import { Clock, LoaderCircle, Send, ShieldAlert } from 'lucide-react';
import { cn } from '@/lib/utils';
import { MAX_LIVE_CHAT_MESSAGE_LENGTH } from '@/services/conversationService';
import { ComposeError, transcribe, type FlaggedFact } from '@/services/composeService';
import ComposeStatus from './compose/ComposeStatus';
import DictationControls from './compose/DictationControls';
import FlaggedBackdrop from './compose/FlaggedBackdrop';
import ImproveControls from './compose/ImproveControls';
import { findFlaggedRanges } from './compose/flaggedRanges';
import { useDictation } from './compose/useDictation';
import { MIN_IMPROVE_WORDS, countWords, useImproveText } from './compose/useImproveText';
import { useStoredToggle } from './compose/useStoredToggle';
import { useTypingHeartbeat } from './compose/useTypingHeartbeat';
import './compose/compose.css';

/** Textarea grows with its content up to this height, then scrolls. */
const MAX_TEXTAREA_HEIGHT_PX = 160;
/** keyCode of a key event the IME is still processing ("Process" key). */
const IME_PROCESS_KEY_CODE = 229;

export const REPLY_WINDOW_CLOSED_HINT =
  'Antwortfenster geschlossen – der Kunde hat seit über 24 Stunden nicht geschrieben.';
/** The WhatsApp number is blocked at Meta: nothing we send arrives (delivery contract 30.09.). */
export const CHANNEL_BLOCKED_HINT =
  'Senden gerade nicht möglich – die WhatsApp-Nummer ist gerade nicht erreichbar. Dein Entwurf bleibt erhalten.';
/** Deadlines come from the salon's handoff settings, so no fixed duration here. */
export const TAKEOVER_ON_SEND_HINT =
  'Mit dem Senden übernimmst du das Gespräch – der Agent pausiert, bis du es zurückgibst oder im Chat eine Weile Ruhe ist.';
/** Per-browser preference „nach Diktat direkt verbessern“ (default on). */
export const AUTO_IMPROVE_STORAGE_KEY = 'tg.liveChat.dictationAutoImprove';

type Props = {
  /** Server says the 24 h window is open (and the thread is WhatsApp). */
  canReply: boolean;
  /** The contact's WhatsApp number is blocked at Meta: no sending, the draft stays. */
  channelBlocked?: boolean;
  takeoverActive: boolean;
  sending: boolean;
  /** Resolves true when the message went out (the composer then clears). */
  onSend: (text: string) => Promise<boolean>;
  /** WhatsApp contact the draft is for (AI helpers use it for context). */
  contactKey?: string | null;
  /** Called at most every 20 s while staff is typing (moves the handoff deadlines). */
  onTypingHeartbeat?: () => void;
};

function isImproveShortcut(e: KeyboardEvent): boolean {
  return (e.metaKey || e.ctrlKey) && !e.altKey && !e.shiftKey && e.key.toLowerCase() === 'j';
}

/**
 * What a key does to a running rewrite: a printable character cancels it
 * and is typed onto the draft; Backspace/Delete/Enter and Android's IME
 * events (keyCode 229, key "Unidentified" — the character is unknown here)
 * only cancel. Shortcuts and navigation keys leave it alone.
 */
function streamingKeyAction(e: KeyboardEvent): 'type' | 'cancel' | null {
  if (e.metaKey || e.ctrlKey || e.altKey) return null;
  if (e.keyCode === IME_PROCESS_KEY_CODE || e.key === 'Unidentified') return 'cancel';
  if (e.key.length === 1) return 'type';
  return e.key === 'Backspace' || e.key === 'Delete' || e.key === 'Enter' ? 'cancel' : null;
}

function uniqueFacts(facts: FlaggedFact[]): FlaggedFact[] {
  const seen = new Set<string>();
  return facts.filter((fact) => {
    const key = `${fact.kind}:${fact.value}`;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

/**
 * Staff reply box at the bottom of a WhatsApp thread. Enter sends,
 * Shift+Enter adds a line; the text stays when sending fails so it can be
 * retried. With a `contactKey` it carries the AI writing help: dictation
 * (speech → text, optionally improved right away) and „Verbessern“ (⌘J /
 * Strg+J), which streams a WhatsApp-ready rewrite into the field, keeps the
 * draft for „Rückgängig“ and marks values the model added.
 */
export function LiveChatComposer({
  canReply: windowOpen,
  channelBlocked = false,
  takeoverActive,
  sending,
  onSend,
  contactKey,
  onTypingHeartbeat,
}: Props) {
  // A blocked number closes the box like a closed reply window does — only
  // the explanation differs. The draft stays in `text` either way.
  const canReply = windowOpen && !channelBlocked;
  const [text, setText] = useState('');
  const textRef = useRef(text);
  textRef.current = text;
  const formRef = useRef<HTMLFormElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const backdropRef = useRef<HTMLDivElement>(null);
  const aiEnabled = canReply && Boolean(contactKey);

  const { notify: notifyTyping, reset: resetHeartbeat } = useTypingHeartbeat(onTypingHeartbeat);
  const improve = useImproveText({ contactKey, setText });
  const { start: startImprove, reset: resetImprove, cancel: cancelImprove, dismissError: dismissImproveError } = improve;
  const [autoImprove, setAutoImprove] = useStoredToggle(AUTO_IMPROVE_STORAGE_KEY, true);
  const autoImproveRef = useRef(autoImprove);
  autoImproveRef.current = autoImprove;

  /** Caret to the end — unless the employee is busy elsewhere on the page. */
  const focusEnd = useCallback(() => {
    const el = textareaRef.current;
    if (!el) return;
    const active = document.activeElement;
    const focusIsFree = !active || active === document.body || Boolean(formRef.current?.contains(active));
    if (!focusIsFree) return;
    el.focus();
    el.setSelectionRange(el.value.length, el.value.length);
  }, []);

  const handleDictated = useCallback(
    (spoken: string) => {
      const base = textRef.current.trimEnd();
      const next = base ? `${base} ${spoken}` : spoken;
      resetImprove(); // a new draft: „Rückgängig“ must not jump back past the dictation
      setText(next);
      notifyTyping();
      if (autoImproveRef.current && countWords(next) >= MIN_IMPROVE_WORDS) void startImprove(next);
      else focusEnd();
    },
    [focusEnd, notifyTyping, resetImprove, startImprove],
  );

  const transcribeForContact = useCallback(
    (audio: Blob, signal: AbortSignal) =>
      contactKey ? transcribe(contactKey, audio, signal) : Promise.reject(new ComposeError('not_found')),
    [contactKey],
  );
  const dictation = useDictation({ transcribe: transcribeForContact, onText: handleDictated });
  const { cancel: cancelDictation, dismissError: dismissDictationError } = dictation;

  const streaming = improve.status === 'streaming';
  const dictationActive = dictation.status !== 'idle';
  const busy = streaming || dictation.status === 'transcribing';
  const trimmed = text.trim();
  const tooLong = trimmed.length > MAX_LIVE_CHAT_MESSAGE_LENGTH;
  // No sending while the microphone is open: the transcript would otherwise
  // land in the NEXT message.
  const canSubmit = canReply && !sending && !streaming && !dictationActive && trimmed.length > 0 && !tooLong;
  const canImprove = aiEnabled && !sending && !busy && !dictationActive && countWords(text) >= MIN_IMPROVE_WORDS;

  const ranges = useMemo(
    () => (improve.status === 'improved' ? findFlaggedRanges(text, improve.flagged) : []),
    [improve.status, improve.flagged, text],
  );
  const visibleFlags = useMemo(() => uniqueFacts(ranges.map((range) => range.fact)), [ranges]);

  // Another chat: nothing typed, streamed or dictated for the previous
  // contact may still be sent to this one.
  const previousContactRef = useRef(contactKey);
  useEffect(() => {
    if (previousContactRef.current === contactKey) return;
    previousContactRef.current = contactKey;
    resetImprove();
    cancelDictation();
    setText('');
    resetHeartbeat();
  }, [contactKey, resetImprove, cancelDictation, resetHeartbeat]);

  // The reply window closed (or the helpers went away): stop, draft back, mic off.
  useEffect(() => {
    if (aiEnabled) return;
    cancelImprove();
    cancelDictation();
  }, [aiEnabled, cancelImprove, cancelDictation]);

  useLayoutEffect(() => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${Math.min(el.scrollHeight, MAX_TEXTAREA_HEIGHT_PX)}px`;
    const backdrop = backdropRef.current;
    if (backdrop) {
      // Classic (non-overlay) scrollbars take width from the textarea only;
      // the mirror gets the same gutter so marks stay under their words.
      backdrop.style.paddingRight = `${Math.max(0, el.offsetWidth - el.clientWidth)}px`;
      backdrop.scrollTop = el.scrollTop;
    }
  }, [text, ranges]);

  useEffect(() => {
    if (improve.status === 'improved') focusEnd();
  }, [improve.status, focusEnd]);

  // Dictating is writing: a recording of up to 2 min must not let the quiet
  // deadline of the takeover run out. The heartbeat itself throttles to 20 s.
  useEffect(() => {
    if (dictation.status === 'recording') notifyTyping();
  }, [dictation.status, dictation.elapsedMs, notifyTyping]);

  // Esc must work even when the focus fell back to <body> (e.g. after a click
  // outside). Inside the form the form's own handler already took care of it.
  useEffect(() => {
    if (!streaming && !dictationActive) return undefined;
    const onKeyDown = (event: globalThis.KeyboardEvent) => {
      if (event.key !== 'Escape' || event.defaultPrevented) return;
      if (event.target !== document.body && event.target !== document.documentElement) return;
      if (streaming) cancelImprove();
      else cancelDictation();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [streaming, dictationActive, cancelImprove, cancelDictation]);

  const runImprove = useCallback(() => {
    if (!canImprove) return;
    dismissDictationError(); // the last action's outcome is what the status line shows
    notifyTyping();
    void startImprove(textRef.current);
  }, [canImprove, dismissDictationError, notifyTyping, startImprove]);

  const submit = useCallback(async () => {
    if (!canSubmit) return;
    const ok = await onSend(trimmed);
    if (ok) {
      setText('');
      resetImprove();
      textareaRef.current?.focus();
    }
  }, [canSubmit, onSend, resetImprove, trimmed]);

  const handleChange = (e: ChangeEvent<HTMLTextAreaElement>) => {
    if (streaming) return;
    setText(e.target.value);
    // An emptied field is a fresh start: no stale „Verbessert · Rückgängig“.
    if (!e.target.value.trim() && improve.status === 'improved') resetImprove();
    notifyTyping();
    if (improve.error) dismissImproveError();
    if (dictation.error) dismissDictationError();
  };

  const handleTextareaKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (streaming) {
      // Typing while the rewrite streams means "never mind": draft back and
      // the typed character is not lost.
      const action = streamingKeyAction(e);
      if (!action) return;
      e.preventDefault();
      cancelImprove();
      if (action === 'type') {
        const typed = e.key;
        setText((draft) => draft + typed);
        notifyTyping();
      }
      return;
    }
    // Never send while an IME composition (e.g. accents) is still open.
    // Safari fires the Enter that confirms a composition with
    // isComposing === false but keyCode 229, so both are checked.
    if (e.key !== 'Enter' || e.shiftKey) return;
    if (e.nativeEvent.isComposing || e.keyCode === IME_PROCESS_KEY_CODE) return;
    e.preventDefault();
    void submit();
  };

  /** Shortcuts for the whole composer (the textarea's events bubble here too). */
  const handleFormKeyDown = (e: KeyboardEvent<HTMLFormElement>) => {
    if (aiEnabled && isImproveShortcut(e)) {
      // Also keeps Ctrl+J from opening the browser's downloads.
      e.preventDefault();
      runImprove();
      return;
    }
    if (e.key !== 'Escape') return;
    if (streaming) {
      e.preventDefault();
      cancelImprove();
    } else if (dictationActive) {
      e.preventDefault();
      cancelDictation();
    }
  };

  const toggleDictation = () => {
    if (dictation.status === 'recording') {
      dictation.stop();
      return;
    }
    dismissImproveError(); // the last action's outcome is what the status line shows
    notifyTyping();
    void dictation.start();
  };

  const selectFlag = (value: string) => {
    const el = textareaRef.current;
    const range = ranges.find((candidate) => candidate.fact.value === value);
    if (!el || !range) return;
    el.focus();
    el.setSelectionRange(range.start, range.end);
  };

  const announcement = streaming
    ? 'Text wird verbessert'
    : dictation.status === 'requesting'
      ? 'Mikrofon wird angefragt'
      : dictation.status === 'recording'
        ? 'Aufnahme läuft'
        : dictation.status === 'transcribing'
          ? 'Aufnahme wird umgewandelt'
          : null;
  const tooLongHint = tooLong
    ? `Zu lang für eine WhatsApp-Nachricht (${trimmed.length.toLocaleString('de-DE')} von ${MAX_LIVE_CHAT_MESSAGE_LENGTH.toLocaleString('de-DE')} Zeichen).`
    : null;

  return (
    <form
      ref={formRef}
      className="mt-3 flex-shrink-0"
      onKeyDown={handleFormKeyDown}
      onSubmit={(e) => {
        e.preventDefault();
        void submit();
      }}
    >
      {channelBlocked ? (
        <p
          role="status"
          className="mb-2 flex items-start gap-2 rounded-md border border-dashed border-destructive/40 bg-destructive/5 px-3 py-2 text-xs leading-snug text-destructive-strong"
        >
          <ShieldAlert className="mt-[1px] h-3.5 w-3.5 flex-shrink-0" aria-hidden="true" />
          {CHANNEL_BLOCKED_HINT}
        </p>
      ) : !windowOpen ? (
        <p
          role="status"
          className="mb-2 flex items-start gap-2 rounded-md border border-dashed border-border bg-background/60 px-3 py-2 text-xs leading-snug text-muted-foreground"
        >
          <Clock className="mt-[1px] h-3.5 w-3.5 flex-shrink-0" aria-hidden="true" />
          {REPLY_WINDOW_CLOSED_HINT}
        </p>
      ) : (
        !takeoverActive && (
          <p className="mb-2 px-1 text-xs leading-snug text-muted-foreground">{TAKEOVER_ON_SEND_HINT}</p>
        )
      )}

      <div
        className={cn(
          'rounded-xl border border-border bg-card shadow-sm transition-[border-color,box-shadow]',
          'focus-within:border-brand/60 focus-within:shadow-[0_0_0_3px_hsl(var(--brand)/0.15)]',
          !canReply && 'bg-muted/60 shadow-none',
        )}
      >
        <div className="flex items-end gap-2 py-1.5 pl-3 pr-1.5">
          <div className="relative min-w-0 flex-1">
            {ranges.length > 0 && <FlaggedBackdrop ref={backdropRef} text={text} ranges={ranges} />}
            <textarea
              ref={textareaRef}
              rows={1}
              value={text}
              onChange={handleChange}
              onKeyDown={handleTextareaKeyDown}
              onScroll={(e) => {
                if (backdropRef.current) backdropRef.current.scrollTop = e.currentTarget.scrollTop;
              }}
              // A closed reply window disables the box. A blocked number only
              // closes it: read-only + aria-disabled keeps the draft focusable
              // and copyable (the hint promises it stays), while nothing can be typed or sent.
              disabled={!windowOpen}
              // Editable while a dictation converts: the transcript is appended to what is there then.
              readOnly={sending || streaming || channelBlocked}
              aria-disabled={channelBlocked || undefined}
              aria-busy={busy}
              maxLength={MAX_LIVE_CHAT_MESSAGE_LENGTH}
              placeholder={canReply ? 'Antwort an den Kunden …' : 'Antworten derzeit nicht möglich'}
              aria-label="Antwort an den Kunden"
              className="relative block max-h-40 min-h-[36px] w-full resize-none bg-transparent py-2 text-sm leading-relaxed text-foreground placeholder:text-muted-foreground focus:outline-none disabled:cursor-not-allowed aria-disabled:cursor-not-allowed aria-disabled:text-muted-foreground"
            />
            {streaming && <div className="compose-shimmer" aria-hidden="true" />}
          </div>
          <button
            type="submit"
            disabled={!canSubmit}
            aria-label={sending ? 'Wird gesendet' : 'Senden'}
            title="Senden (Enter) · neue Zeile mit Shift+Enter"
            className={cn(
              'inline-flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg bg-brand text-brand-foreground transition-all',
              '[@media(pointer:coarse)]:h-10 [@media(pointer:coarse)]:w-10',
              'hover:bg-brand-hover active:scale-95',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background',
              'disabled:cursor-not-allowed disabled:bg-muted-foreground/30 disabled:text-background disabled:active:scale-100',
            )}
          >
            {sending ? (
              <LoaderCircle className="h-4 w-4 animate-spin" aria-hidden="true" />
            ) : (
              <Send className="h-4 w-4" aria-hidden="true" />
            )}
          </button>
        </div>

        {aiEnabled && (
          <>
            <div className="flex min-h-[40px] items-center gap-1 border-t border-border/70 px-1.5 py-1">
              <DictationControls
                status={dictation.status}
                elapsedMs={dictation.elapsedMs}
                // A running recording must stay stoppable, whatever else happens.
                disabled={!dictationActive && (sending || streaming)}
                autoImprove={autoImprove}
                onAutoImproveChange={setAutoImprove}
                onToggle={toggleDictation}
                onCancel={cancelDictation}
                getLevel={dictation.getLevel}
              />
              {!dictationActive && (
                <ImproveControls
                  status={improve.status}
                  disabled={!canImprove}
                  onImprove={runImprove}
                  onCancel={cancelImprove}
                />
              )}
            </div>
            <ComposeStatus
              error={improve.error ?? dictation.error}
              announcement={announcement}
              improved={improve.status === 'improved'}
              flagged={visibleFlags}
              tooLongHint={tooLongHint}
              onUndo={() => {
                improve.undo();
                textareaRef.current?.focus();
              }}
              onSelectFlag={selectFlag}
            />
          </>
        )}
      </div>
    </form>
  );
}

export default LiveChatComposer;
